import { inject, Injectable } from '@angular/core';

import { map, Observable, of } from 'rxjs';

import { TimeService } from '@core/common-legacy';
import {
  AssignMembersOptions,
  ConfirmPositionChangeDialogResult,
  ConfirmPositionChangeOutcome,
} from '@core/tellent-positions-types';

import { resolveAssignmentFailureReason } from './position-assignment-error.util';
import { TellentPositionsDataFacade } from './tellent-positions-data.facade';

// Only the two fields both requests answer with matter here; a success action carries no payload at
// all, so `action` stays untyped and the payload is read where it is needed.
type ActionResponse = { isSuccess: boolean; action: object };

/**
 * Turns a confirmed position change into the request it implies, and both into one outcome shape.
 *
 * Adding to history moves the members from the chosen date, so it goes to `assign`. A correction
 * only restates which position they hold, so it goes to `reassign` and deliberately sends no date —
 * the backend defaults it, and inferring one the user never gave would be inventing it.
 *
 * Shared by the positions UI and the `tellent-client` wrapper that drives the same dialog from
 * Manage, so the branching and the failure-message rule live in one place.
 */
@Injectable({ providedIn: 'root' })
export class PositionChangeApplyService {
  private readonly positionsDataFacade = inject(TellentPositionsDataFacade);
  private readonly timeService = inject(TimeService);

  public apply(
    positionGuid: string,
    membersGuids: string[],
    result: ConfirmPositionChangeDialogResult,
  ): Observable<ConfirmPositionChangeOutcome> {
    if (!result?.confirmed) {
      return of<ConfirmPositionChangeOutcome>({ status: 'cancelled', isScheduled: false });
    }

    if (!result.addToHistory) {
      return this.toOutcome(
        this.positionsDataFacade.correctMembersPosition(positionGuid, membersGuids),
        false,
      );
    }

    return this.assign(positionGuid, membersGuids, { startDate: result.effectiveDate });
  }

  public assign(
    positionGuid: string,
    membersGuids: string[],
    options?: AssignMembersOptions,
  ): Observable<ConfirmPositionChangeOutcome> {
    const isScheduled = !!options && this.timeService.isAfterToday(options.startDate);

    return this.toOutcome(
      this.positionsDataFacade.assignMembers(positionGuid, membersGuids, options),
      isScheduled,
    );
  }

  private toOutcome(
    request$: Observable<ActionResponse>,
    isScheduled: boolean,
  ): Observable<ConfirmPositionChangeOutcome> {
    return request$.pipe(
      map(
        (response): ConfirmPositionChangeOutcome =>
          response.isSuccess
            ? { status: 'success', isScheduled }
            : {
                status: 'failed',
                isScheduled,
                errorMessage:
                  resolveAssignmentFailureReason(
                    (<{ payload?: unknown }>response.action).payload,
                  ) ?? undefined,
              },
      ),
    );
  }
}
